/*
 * Hand a file Fortress generated (a memo .docx, a workbook) to the user INSIDE Outlook.
 *
 * Outlook's task pane cannot download: it is a webview with no download manager, so
 * a scripted save renders the file's bytes as text ("wingdings"), and the browser
 * routes are dead too — `Office.context.ui.openBrowserWindow` is not available to an
 * Outlook add-in (silent no-op, proven 2026-08-18 on the Automations button) and
 * `window.open` lands in a sessionless embedded webview. What Outlook CAN do natively
 * is attach a file from a URL, so that is how the file reaches the user: attached to
 * the draft they are writing, or to a new message, from which it opens in Word.
 *
 * The URL must be reachable without the app's auth (Outlook fetches it itself): the
 * view passes a short-lived presigned link from /api/v1/office/documents/{id}/link.
 */

export type DeliverResult =
  | { ok: true; mode: "attached_to_draft" | "new_message" }
  | { ok: false; error: string };

/** Compose items expose write methods on body (`setAsync`); read items do not. */
function isCompose(item: any): boolean {
  return !!(item && item.body && typeof item.body.setAsync === "function");
}

export function deliverFile(mailbox: any, url: string, filename: string): Promise<DeliverResult> {
  if (typeof url !== "string" || !/^https:\/\//i.test(url)) {
    return Promise.resolve({ ok: false, error: "no https link to the file" });
  }
  const name = (typeof filename === "string" && filename.trim()) || "document";
  const item = mailbox?.item;

  // Writing an email: attach to that draft.
  if (isCompose(item) && typeof item.addFileAttachmentAsync === "function") {
    return new Promise((resolve) => {
      item.addFileAttachmentAsync(url, name, {}, (res: any) => {
        if (res && res.status === "succeeded") resolve({ ok: true, mode: "attached_to_draft" });
        else resolve({ ok: false, error: String(res?.error?.message ?? "Outlook could not attach the file") });
      });
    });
  }

  // Reading (or no item): a new message carrying the file.
  if (mailbox && typeof mailbox.displayNewMessageForm === "function") {
    try {
      mailbox.displayNewMessageForm({
        subject: name.replace(/\.[a-z0-9]+$/i, ""),
        attachments: [{ type: "file", name, url }],
      });
      return Promise.resolve({ ok: true, mode: "new_message" });
    } catch (err: any) {
      return Promise.resolve({ ok: false, error: String(err?.message ?? err) });
    }
  }
  return Promise.resolve({ ok: false, error: "this Outlook cannot attach files from an add-in" });
}
